import { t, type StaffKey, type StaffLanguage } from '../lib/staffText'
import { hasCalled } from './calledScreen'
import {
  onScreen,
  placeholderKeys,
  type CounterScreen,
  type DisplayScreen,
  type ElementKind,
  type Fault,
  type KioskScreen,
  type LayoutDocument,
  type LayoutKind,
  type Screen,
} from './types'

/** A counter display's screens, in the order the editor lists them. */
export const COUNTER_SCREENS: readonly CounterScreen[] = ['called', 'free']

const KIOSK_SCREENS: readonly KioskScreen[] = ['attract', 'choosing', 'issued']

/** A main display's board and its Called screen (calledScreen.ts). */
export const DISPLAY_SCREENS: readonly DisplayScreen[] = ['board', 'call']

/** Kinds that belong on one counter screen only (the server's ValidateWith). */
export const COUNTER_HOME: Partial<Record<ElementKind, CounterScreen>> = {
  ticketNumber: 'called',
  serviceName: 'called',
  deskStatus: 'free',
}

/** The screens a kind of design has; empty for one that has a single screen. */
export function screensOf(kind: LayoutKind): readonly Screen[] {
  if (kind === 'kiosk') return KIOSK_SCREENS
  if (kind === 'counter') return COUNTER_SCREENS
  if (kind === 'display') return DISPLAY_SCREENS
  return []
}

/** The screen a design opens on, and the one a thumbnail draws. */
export function firstScreen(kind: LayoutKind): Screen | undefined {
  if (kind === 'kiosk') return 'choosing'
  if (kind === 'counter') return 'called'
  if (kind === 'display') return 'board'
  return undefined
}

/** The screen being drawn on, for a kind that has screens; a stale one, or a removed
 *  Called screen, falls back to the first. */
export function drawnScreen(doc: Pick<LayoutDocument, 'kind' | 'called'>, screen: Screen): Screen | undefined {
  const all = screensOf(doc.kind)
  if (all.length === 0) return undefined
  if (screen === 'call' && !hasCalled(doc)) return firstScreen(doc.kind)
  return all.includes(screen) ? screen : firstScreen(doc.kind)
}

/** What the server refuses about a counter design's screens, in the editor's words;
 *  and, on any other kind, a counter screen name. */
export function counterFaults(doc: LayoutDocument, lang: StaffLanguage = 'en'): Fault[] {
  const found: Fault[] = []
  const counter = doc.kind === 'counter'
  const foreign = (s: Screen) => (COUNTER_SCREENS as readonly Screen[]).includes(s) !== counter
  for (const el of doc.elements) {
    const say = (text: string) => found.push({ text, ids: [el.id] })
    // editing.ts's ELEMENT_LABELS, read by key: editing imports this module.
    const label = t(`designs.el.${el.kind}` as StaffKey, lang)
    if ((el.screens ?? []).some(foreign)) say(t('designs.fault.otherScreen', lang, { label }))
    if (!counter) continue
    const home = COUNTER_HOME[el.kind]
    if (home && !(el.screens?.length === 1 && el.screens[0] === home)) {
      say(t(home === 'called' ? 'designs.fault.calledOnly' : 'designs.fault.freeOnly', lang, { label }))
    }
    if (el.kind === 'text' && onScreen(el, 'free') && Object.values(el.content ?? {}).some((v) => placeholderKeys(v).length > 0)) {
      say(t('designs.fault.fieldsOnFree', lang))
    }
  }
  return found
}

/** The look a counter template draws, from its name: "signal" of "signal-1280x400". */
export function counterLook(name: string): string {
  return name.replace(/-\d+x\d+$/, '')
}

/** The look New design offers first and picks: the owner's Desk sign (29 Oct). The
 *  built-in stays Simple, which names no clerk. */
export const FIRST_LOOK = 'sign'

/** One start per look, Desk sign first and the rest in the server's order: the look's
 *  template of the chosen size, else its first redrawn for it. */
export function counterStarts<T extends { name: string; document: LayoutDocument }>(
  templates: readonly T[],
  size: { width: number; height: number },
  resize: (doc: LayoutDocument, size: { width: number; height: number }) => LayoutDocument,
): Array<{ template: T; document: LayoutDocument }> {
  const all = Array.from(new Set(templates.map((tpl) => counterLook(tpl.name))))
  const looks = all.includes(FIRST_LOOK) ? [FIRST_LOOK, ...all.filter((l) => l !== FIRST_LOOK)] : all
  return looks.map((look) => {
    const own = templates.filter((tpl) => counterLook(tpl.name) === look)
    const exact = own.find((tpl) => tpl.name === `${look}-${size.width}x${size.height}`)
    if (exact) return { template: exact, document: exact.document }
    const first = own[0]!
    return { template: first, document: resize(first.document, size) }
  })
}
